import React, { createContext, useContext, useMemo, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useGetVisibilityKpisQuery } from 'src/context/api/competitorsMonitoring/api';
import { fDate } from 'src/utils/format-time';
import countriesJson from 'src/utils/countries.json';
import { deriveCountryCode } from '../hooks/countryCode';
import { CountryItem } from 'src/components/country-bar/types';

// Context to share common data across visibility components
interface VisibilityContextValue {
    // Base data
    visibilityData: any[];
    isLoadingBaseData: boolean;
    brandTrafficShareByName: Map<string, number>;

    // Processed country data
    countriesData: CountryItem[];
    countryLevelByName: Map<string, string>;
    selectedCountry: string | null;
    setSelectedCountry: (country: string | null) => void;

    // Helper
    skipQuery: boolean;
    workspace: any;
    filterStore: any;
}

export const VisibilityContext = createContext<VisibilityContextValue | null>(null);

export const VisibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const existingContext = useContext(VisibilityContext);
    if (existingContext) {
        return <>{children}</>;
    }

    const workspace: any = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);
    const skipQuery = !filterStore.start || !filterStore.end || !workspace?.id;

    const { data: visibilityDataRaw, isFetching, isUninitialized } = useGetVisibilityKpisQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, "yyyy-MM-dd") : null,
        compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, "yyyy-MM-dd") : null,
        timestep: filterStore.timestep,
        devices: filterStore.devices,
        countries: filterStore.countries,
        keywords: [],
        platforms: filterStore?.platforms,
        workspaceId: workspace?.id,
    }, {
        skip: skipQuery,
    });
    const isLoadingBaseData = isFetching || (isUninitialized && !skipQuery);

    const ciItems: any[] = useMemo(() =>
        Array.isArray(visibilityDataRaw) ? visibilityDataRaw : [],
        [visibilityDataRaw]
    );

    const countriesList = useMemo(() => {
        const uniqueCountryNames = Array.from(new Set(ciItems.map((it: any) => it?.country_name).filter(Boolean)));
        return uniqueCountryNames.map((name: string) => ({
            country_name: name,
            country_code: deriveCountryCode(name),
        }));
    }, [ciItems]);

    const countryLevelByName = useMemo(() => {
        const severityRank: Record<string, number> = { low: 0, active: 1, high: 2 };
        const levels = new Map<string, string>();
        for (const item of ciItems) {
            const countryName: string | undefined = (item?.country_name ?? item?.country) as string | undefined;
            const level: string | undefined = (item?.level ?? item?.abs_level ?? item?.severity_level) as string | undefined;
            if (!countryName || !level) continue;
            const current = levels.get(countryName);
            if (!current || (severityRank[level] ?? -1) > (severityRank[current] ?? -1)) {
                levels.set(countryName, level);
            }
        }
        return levels;
    }, [ciItems]);

    const shareByCountry = useMemo(() => {
        const map = new Map<string, number>();
        for (const item of ciItems) {
            if (!item?.country_name) continue;
            const share =
                typeof item?.brand_traffic_share === 'number'
                    ? item.brand_traffic_share
                    : (typeof item?.share_of_brand_searches === 'number'
                        ? item.share_of_brand_searches
                        : null);
            if (typeof share === 'number') {
                map.set(item.country_name, share);
            }
        }
        return map;
    }, [ciItems]);

    const countriesData: CountryItem[] = useMemo(() => {
        return countriesList
            .map(country => ({
                ...country,
                level: countryLevelByName.get(country.country_name),
                share_of_brand_searches: shareByCountry.get(country.country_name) ?? null,
            }))
            .sort((a, b) => (b.share_of_brand_searches ?? 0) - (a.share_of_brand_searches ?? 0));
    }, [countriesList, countryLevelByName, shareByCountry]);

    const brandTrafficShareByName = useMemo(() => {
        const map = new Map<string, number>();
        for (const item of ciItems) {
            const countryName = item?.country_name;
            const share = item?.brand_traffic_share;
            if (countryName != null && typeof share === 'number') {
                map.set(countryName, share);
            }
        }
        return map;
    }, [ciItems]);

    const [selectedCountry, setSelectedCountryState] = useState<string | null>(null);
    const initializedWorkspaceRef = useRef<number | string | null>(null);

    const setSelectedCountry = (country: string | null) => {
        setSelectedCountryState(country);
        try {
            if (workspace?.id) {
                const key = `visibility.selectedCountry.${workspace.id}`;
                if (country) localStorage.setItem(key, country);
                else localStorage.removeItem(key);
            }
        } catch {}
    };

    // The selection is re-resolved whenever it stops being reachable: switching
    // workspace keeps this provider mounted, and a date-range / platform change can
    // drop countries from the list. Without this the bar keeps a country that is no
    // longer in `countriesData`, and no tab shows up as selected.
    useEffect(() => {
        if (!workspace?.id) return;
        if (!countriesData || countriesData.length === 0) return;

        const workspaceChanged = initializedWorkspaceRef.current !== workspace.id;
        const stillAvailable =
            !!selectedCountry && countriesData.some((c) => c.country_name === selectedCountry);
        if (!workspaceChanged && stillAvailable) return;

        let initial: string | null = null;
        try {
            const params = new URLSearchParams(window.location.search);
            const codeParam = params.get('country');
            if (codeParam) {
                const nameFromCode = countriesJson?.find((c: any) => String(c.code).toUpperCase() === String(codeParam).toUpperCase())?.name;
                if (nameFromCode && countriesData.some((c) => c.country_name === nameFromCode)) {
                    initial = nameFromCode;
                }
            }
        } catch {}
        if (!initial) {
            try {
                const key = `visibility.selectedCountry.${workspace.id}`;
                const fromStorage = localStorage.getItem(key);
                if (fromStorage && countriesData.some((c) => c.country_name === fromStorage)) {
                    initial = fromStorage;
                }
            } catch {}
        }
        if (!initial) {
            initial = countriesData[0]?.country_name ?? null;
        }
        initializedWorkspaceRef.current = workspace.id;
        setSelectedCountry(initial);
    }, [countriesData, workspace?.id, selectedCountry]);

    const contextValue: VisibilityContextValue = {
        visibilityData: ciItems,
        isLoadingBaseData,
        brandTrafficShareByName,
        countriesData,
        countryLevelByName,
        selectedCountry,
        setSelectedCountry,
        skipQuery,
        workspace,
        filterStore,
    };

    return (
        <VisibilityContext.Provider value={contextValue}>
            {children}
        </VisibilityContext.Provider>
    );
};

export const useVisibilityContext = () => {
    const context = useContext(VisibilityContext);
    if (!context) {
        throw new Error('useVisibilityContext must be used within VisibilityProvider');
    }
    return context;
};
